import React, { useCallback, useEffect, useState } from 'react';
import {
    fetchInspections, createInspection, Inspection, InspectionTemplate, InspectionSchedule,
} from '../../services/inspectionsApi';
import { btn, btnSmall, inGroup, StatusBadge } from './common';
import { ui } from '../../styles/shared';
import { fmtDate, todayStr } from '../../utils/format';

/**
 * Fliken "Att göra": öppna kontroller, åtgärder som väntar och kommande scheman.
 * Laddar sina egna listor; laddar om när refreshKey ändras i föräldern.
 */
interface Props {
    group: string;                 // '' = alla grupper
    templates: InspectionTemplate[];
    schedules: InspectionSchedule[];
    /** Räknas upp av föräldern när något sparats, så listorna hämtas igen. */
    refreshKey: number;
    /** Öppnar en kontroll i panelen ovanför flikarna. */
    onOpen: (id: number) => Promise<void>;
    /** Laddar om mallar, scheman och räknare i föräldern. */
    reload: () => Promise<void>;
    onError: (msg: string) => void;
}

const TodoTab: React.FC<Props> = ({ group, templates, schedules, refreshKey, onOpen, reload, onError }) => {
    const [open, setOpen] = useState<Inspection[]>([]);
    const [actions, setActions] = useState<Inspection[]>([]);
    const [pickTemplate, setPickTemplate] = useState(false);
    // Ronder som är utfällda i listan (visar platserna).
    const [openRounds, setOpenRounds] = useState<Set<number>>(new Set());
    const [pickSchedulesFor, setPickSchedulesFor] = useState<InspectionTemplate | null>(null);

    const load = useCallback(async () => {
        try {
            const [o, a] = await Promise.all([fetchInspections({ status: 'open' }), fetchInspections({ status: 'actions' })]);
            setOpen(o); setActions(a);
        } catch (e: any) { onError(e.message); }
    }, [onError]);

    useEffect(() => { load(); }, [load, refreshKey]);

    /** Ett rondschema ger en kontroll per plats; då öppnas ingen enskild kontroll utan listan laddas om. */
    const openCreated = async (r: { id: number | null; round_id?: number }) => {
        if (r.id != null) await onOpen(r.id);
        else if (r.round_id) setOpenRounds(prev => new Set(prev).add(r.round_id!));
        await reload();
        await load();
    };

    const startFromSchedule = async (s: InspectionSchedule) => {
        try {
            await openCreated(await createInspection({ schedule_id: s.id }));
        } catch (e: any) { onError(e.message); }
    };

    /**
     * "Gör en kontroll nu": har mallen ett aktivt schema används det, så att plats,
     * ansvarig och påminnelser följer med. Finns flera scheman får man välja;
     * finns inget skapas kontrollen direkt från mallen.
     */
    const startFromTemplate = async (t: InspectionTemplate) => {
        const linked = schedules.filter(s => s.active && s.template_id === t.id);
        if (linked.length > 1) { setPickSchedulesFor(t); return; }
        setPickTemplate(false);
        setPickSchedulesFor(null);
        try {
            const r = linked.length === 1
                ? await createInspection({ schedule_id: linked[0].id })
                : await createInspection({ template_id: t.id });
            await openCreated(r);
        } catch (e: any) { onError(e.message); }
    };

    const gOpen = open.filter(i => inGroup(group, i.group));
    // Ronder: en rad per rond med platserna under. Enskilda kontroller som förut.
    type OpenRow = { kind: 'single'; i: Inspection } | { kind: 'round'; id: number; i: Inspection; items: Inspection[] };
    const openRows: OpenRow[] = [];
    const seenRounds = new Map<number, OpenRow>();
    for (const i of gOpen) {
        if (i.round_id == null) { openRows.push({ kind: 'single', i }); continue; }
        const row = seenRounds.get(i.round_id);
        if (row && row.kind === 'round') row.items.push(i);
        else { const r: OpenRow = { kind: 'round', id: i.round_id, i, items: [i] }; seenRounds.set(i.round_id, r); openRows.push(r); }
    }
    const toggleRound = (id: number) => setOpenRounds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    const gActions = actions.filter(i => inGroup(group, i.group));
    const gTemplates = templates.filter(t => inGroup(group, t.category));
    const upcoming = schedules.filter(s => inGroup(group, s.group) && s.active && s.open_count === 0);

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setPickTemplate(v => !v)} style={btn('#28a745')} disabled={!templates.length}>+ Gör en kontroll nu</button>
                {!templates.length && <span style={{ fontSize: 13, color: '#888' }}>Skapa en mall först under fliken Mallar.</span>}
            </div>
            {pickTemplate && (
                <div style={ui.card}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Välj mall</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {gTemplates.filter(t => t.active).map(t => {
                            const n = schedules.filter(s => s.active && s.template_id === t.id).length;
                            return (
                                <button key={t.id} onClick={() => startFromTemplate(t)} style={btnSmall(pickSchedulesFor?.id === t.id ? '#1a4aa6' : '#0f3460')}
                                    title={n === 1 ? 'Använder mallens schema' : n > 1 ? 'Mallen har flera scheman, välj plats' : 'Utan schema och plats'}>
                                    {t.name}{n > 0 && <span style={{ opacity: .7, marginLeft: 6, fontSize: 11 }}>{n} schema{n === 1 ? '' : 'n'}</span>}
                                </button>
                            );
                        })}
                    </div>
                    {pickSchedulesFor && (
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 14 }}>Var ska kontrollen göras?</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {schedules.filter(s => s.active && s.template_id === pickSchedulesFor.id).map(s => (
                                    <button key={s.id} onClick={() => { setPickTemplate(false); setPickSchedulesFor(null); startFromSchedule(s); }} style={btnSmall('#e8f0fe', '#1a4aa6')}>
                                        {s.name}{s.is_round ? ` · rond, ${s.place_count} platser` : s.zone_name ? ` · ${s.zone_name}` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {gOpen.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Inget att göra just nu. Nästa kontroll skapas automatiskt några dagar innan den ska göras.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: 8 }}>
                        <thead><tr><th style={ui.th}>Senast</th><th style={ui.th}>Kontroll</th><th style={ui.th}>Plats</th><th style={ui.th}>Status</th><th style={ui.th}></th></tr></thead>
                        <tbody>
                            {openRows.map(row => row.kind === 'single' ? (
                                <tr key={row.i.id} style={{ background: row.i.status === 'overdue' ? '#fff5f5' : undefined }}>
                                    <td style={ui.td}>{fmtDate(row.i.due_date)}</td>
                                    <td style={ui.td}><b>{row.i.schedule_name ?? row.i.template_name}</b><div style={{ fontSize: 12, color: '#777' }}>{row.i.template_name}</div></td>
                                    <td style={ui.td}>{row.i.zone_name ?? '–'}</td>
                                    <td style={ui.td}><StatusBadge i={row.i} />{row.i.started_by_name && row.i.has_draft && <div style={{ fontSize: 12, color: '#777', marginTop: 3 }}>av {row.i.started_by_name}</div>}</td>
                                    <td style={{ ...ui.td, textAlign: 'right' }}><button onClick={() => onOpen(row.i.id)} style={btnSmall('#0f3460')}>{row.i.has_draft ? 'Fortsätt' : 'Gör kontrollen'}</button></td>
                                </tr>
                            ) : (
                                <React.Fragment key={`round-${row.id}`}>
                                    <tr style={{ background: row.i.status === 'overdue' ? '#fff5f5' : undefined }}>
                                        <td style={ui.td}>{fmtDate(row.i.due_date)}</td>
                                        <td style={ui.td}><b>{row.i.schedule_name ?? row.i.template_name}</b><div style={{ fontSize: 12, color: '#777' }}>{row.i.template_name} · rond</div></td>
                                        <td style={ui.td}>{row.i.round_total ?? row.items.length} platser</td>
                                        <td style={ui.td}>
                                            <StatusBadge i={row.i} />
                                            <div style={{ fontSize: 12, color: '#777', marginTop: 3 }}>{row.i.round_done ?? 0} av {row.i.round_total ?? row.items.length} klara</div>
                                        </td>
                                        <td style={{ ...ui.td, textAlign: 'right' }}>
                                            <button onClick={() => toggleRound(row.id)} style={btnSmall('#0f3460')}>{openRounds.has(row.id) ? 'Dölj platser' : 'Visa platser'}</button>
                                        </td>
                                    </tr>
                                    {openRounds.has(row.id) && row.items.map(i => (
                                        <tr key={i.id} style={{ background: '#fafbfc' }}>
                                            <td style={ui.td}></td>
                                            <td style={{ ...ui.td, paddingLeft: 28, color: '#555' }}>{i.zone_name ?? 'Plats'}</td>
                                            <td style={ui.td}></td>
                                            <td style={ui.td}><StatusBadge i={i} />{i.started_by_name && i.has_draft && <div style={{ fontSize: 12, color: '#777', marginTop: 3 }}>av {i.started_by_name}</div>}</td>
                                            <td style={{ ...ui.td, textAlign: 'right' }}><button onClick={() => onOpen(i.id)} style={btnSmall('#e8f0fe', '#1a4aa6')}>{i.has_draft ? 'Fortsätt' : 'Gör kontrollen'}</button></td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {gActions.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 8, color: '#555' }}>Åtgärder som väntar</div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                            <thead><tr><th style={ui.th}>Klart senast</th><th style={ui.th}>Åtgärd</th><th style={ui.th}>Kontroll</th><th style={ui.th}>Status</th><th style={ui.th}></th></tr></thead>
                            <tbody>
                                {gActions.map(i => (
                                    <tr key={i.id} style={{ background: i.action_due_date && i.action_due_date < todayStr() ? '#fff5f5' : undefined }}>
                                        <td style={ui.td}>{fmtDate(i.action_due_date)}</td>
                                        <td style={ui.td}>{i.action_note}<div style={{ fontSize: 12, color: '#777' }}>anmält av {i.completed_by_name ?? 'okänd'}</div></td>
                                        <td style={ui.td}><b>{i.schedule_name ?? i.template_name}</b><div style={{ fontSize: 12, color: '#777' }}>{fmtDate(i.due_date)}{i.zone_name ? ` · ${i.zone_name}` : ''}</div></td>
                                        <td style={ui.td}><StatusBadge i={i} /></td>
                                        <td style={{ ...ui.td, textAlign: 'right' }}><button onClick={() => onOpen(i.id)} style={btnSmall('#fd7e14')}>Klarmarkera</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {upcoming.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 8, color: '#555' }}>Kommande</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {upcoming.map(s => (
                            <div key={s.id} style={{ ...ui.card, marginBottom: 0, padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'center' }}>
                                <div><b>{s.name}</b><div style={{ fontSize: 12, color: '#777' }}>nästa {fmtDate(s.next_due_date)}</div></div>
                                <button onClick={() => startFromSchedule(s)} style={btnSmall('#e8f0fe', '#1a4aa6')}>Gör i förtid</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TodoTab;
